/*
 * Size Guide — modal, tabs, chart rendering, and size recommender.
 *
 * This script is the single source of truth for the recommendation algorithm.
 * Do NOT ship the `recommendationFunction` field inside per-product JSON —
 * the math is fully determined by `sizeOrder`, `recommendationRules.heightWeightBands`,
 * and (optionally) `recommendationRules.fitAdjustments`.
 *
 * Init:
 *   DOM is scanned for `[data-dg-size-guide]` roots on DOMContentLoaded, and each
 *   root is independently wired. Inside each root we expect:
 *     - a `<script type="application/json" data-dg-size-guide-config>` with the
 *       parsed size-guide metafield content,
 *     - a trigger button `[data-dg-size-guide-open]`,
 *     - an overlay `.dg-size-guide__overlay`,
 *     - tab buttons `[data-dg-size-guide-tab]` / tab panels `[data-dg-size-guide-content]`,
 *     - a chart container `[data-dg-size-guide-chart]`,
 *     - recommender inputs under `[data-dg-size-guide-form]`.
 */

(function () {
  'use strict';

  /** Default fit offsets applied when a metafield omits them. */
  var DEFAULT_ADJUSTMENTS = { tight: -1, normal: 0, baggy: 1 };

  /**
   * Generic size recommender. Finds the first height/weight band where the
   * user fits under BOTH the height and weight caps, then shifts by the
   * requested fit offset, clamped to the sizeOrder bounds.
   *
   * @param {number} heightInches
   * @param {number} weightPounds
   * @param {object} config  Parsed size_guide metafield
   * @returns {{tight:string,normal:string,baggy:string}|null}
   */
  function getRecommendedSizes(heightInches, weightPounds, config) {
    if (!config) return null;
    var sizeOrder = Array.isArray(config.sizeOrder) ? config.sizeOrder : [];
    var rules = config.recommendationRules || {};
    var bands = Array.isArray(rules.heightWeightBands) ? rules.heightWeightBands : [];
    var adjustments = rules.fitAdjustments || DEFAULT_ADJUSTMENTS;

    if (!sizeOrder.length || !bands.length) return null;
    if (!Number.isFinite(heightInches) || !Number.isFinite(weightPounds)) return null;

    // Bands are expected smallest -> largest; fall back to the last band if
    // we never find a match (i.e. user exceeds every cap).
    var normalSize = bands[bands.length - 1].normalSize;
    for (var i = 0; i < bands.length; i++) {
      var band = bands[i];
      if (heightInches <= band.maxHeight && weightPounds <= band.maxWeight) {
        normalSize = band.normalSize;
        break;
      }
    }

    function shift(size, amount) {
      var idx = sizeOrder.indexOf(size);
      if (idx === -1) return size;
      var target = Math.max(0, Math.min(sizeOrder.length - 1, idx + amount));
      return sizeOrder[target];
    }

    return {
      tight: shift(normalSize, numOr(adjustments.tight, DEFAULT_ADJUSTMENTS.tight)),
      normal: shift(normalSize, numOr(adjustments.normal, DEFAULT_ADJUSTMENTS.normal)),
      baggy: shift(normalSize, numOr(adjustments.baggy, DEFAULT_ADJUSTMENTS.baggy)),
    };
  }

  function numOr(v, fallback) {
    return Number.isFinite(v) ? v : fallback;
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /** "sleeveLength" -> "Sleeve length"; "chest" -> "Chest" */
  function humanizeKey(key) {
    var withSpaces = String(key).replace(/([a-z])([A-Z])/g, '$1 $2');
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();
  }

  function renderChart(container, config) {
    var chart = (config && Array.isArray(config.sizeChart)) ? config.sizeChart : [];
    if (!chart.length) {
      container.innerHTML = '<p class="dg-size-guide__empty">Size chart unavailable.</p>';
      return;
    }

    var unit = (config && config.metadata && config.metadata.units && config.metadata.units.measurements) || 'inches';

    // Measurement columns are discovered from the first chart row so new fields
    // (e.g. "waist") flow through without any theme changes.
    var columns = Object.keys(chart[0]).filter(function (k) { return k !== 'size'; });

    var headerCells = ['<th scope="col">Size</th>'].concat(columns.map(function (c) {
      return '<th scope="col">' + escapeHtml(humanizeKey(c)) + '</th>';
    })).join('');

    var rowsHtml = chart.map(function (row) {
      var cells = columns.map(function (c) {
        var v = row[c];
        return '<td>' + (v == null || v === '' ? '&mdash;' : escapeHtml(String(v))) + '</td>';
      }).join('');
      return '<tr><th scope="row">' + escapeHtml(String(row.size)) + '</th>' + cells + '</tr>';
    }).join('');

    container.innerHTML =
      '<div class="dg-size-guide__chart-scroll">' +
        '<table class="dg-size-guide__table" aria-label="Size chart">' +
          '<thead><tr>' + headerCells + '</tr></thead>' +
          '<tbody>' + rowsHtml + '</tbody>' +
        '</table>' +
      '</div>' +
      '<p class="dg-size-guide__chart-note">All measurements in ' + escapeHtml(unit) + '.</p>';
  }

  function init(root) {
    if (!root || root.dataset.dgSizeGuideInit === '1') return;
    root.dataset.dgSizeGuideInit = '1';

    var cfgEl = root.querySelector('[data-dg-size-guide-config]');
    var config = null;
    try {
      config = cfgEl ? JSON.parse(cfgEl.textContent.trim()) : null;
    } catch (e) {
      // If the JSON is malformed, hide the trigger so users don't hit a broken modal.
      console.warn('[DG] size-guide: failed to parse config', e);
      var brokenTrigger = root.querySelector('[data-dg-size-guide-open]');
      if (brokenTrigger) brokenTrigger.hidden = true;
      return;
    }

    var overlay = root.querySelector('.dg-size-guide__overlay');
    var panel = root.querySelector('[data-dg-size-guide-panel]');
    var openBtn = root.querySelector('[data-dg-size-guide-open]');
    var closeBtn = root.querySelector('[data-dg-size-guide-close]');
    var tabs = root.querySelectorAll('[data-dg-size-guide-tab]');
    var contents = root.querySelectorAll('[data-dg-size-guide-content]');
    var chartContainer = root.querySelector('[data-dg-size-guide-chart]');

    if (!overlay || !panel || !openBtn) return;

    if (chartContainer) renderChart(chartContainer, config);

    // ---- Open / close ------------------------------------------------------
    var lastFocused = null;

    function focusablesIn(el) {
      return Array.prototype.slice.call(el.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(function (n) { return !n.hidden && n.offsetParent !== null; });
    }

    function onKeyDown(e) {
      if (overlay.hidden) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      // Simple focus trap.
      var nodes = focusablesIn(panel);
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function open() {
      lastFocused = document.activeElement;
      overlay.hidden = false;
      document.body.classList.add('dg-size-guide-open');
      // Next frame so the browser picks up the `hidden` removal before we add
      // the `is-open` class (which drives the CSS transition).
      requestAnimationFrame(function () {
        overlay.classList.add('is-open');
        panel.classList.add('is-open');
        (closeBtn || panel).focus();
      });
      openBtn.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', onKeyDown, true);
    }

    function close() {
      overlay.classList.remove('is-open');
      panel.classList.remove('is-open');
      document.body.classList.remove('dg-size-guide-open');
      openBtn.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', onKeyDown, true);
      // Wait out the CSS transition before restoring `hidden` and focus.
      setTimeout(function () {
        overlay.hidden = true;
        if (lastFocused && typeof lastFocused.focus === 'function') {
          try { lastFocused.focus({ preventScroll: true }); } catch (_) { lastFocused.focus(); }
        }
      }, 220);
    }

    openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) {
      // Clicks on the backdrop (not inside the panel) close the modal.
      if (e.target === overlay) close();
    });

    // ---- Tab switching -----------------------------------------------------
    function activateTab(name) {
      tabs.forEach(function (t) {
        var active = t.getAttribute('data-dg-size-guide-tab') === name;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', String(active));
        t.tabIndex = active ? 0 : -1;
      });
      contents.forEach(function (c) {
        var active = c.getAttribute('data-dg-size-guide-content') === name;
        c.classList.toggle('is-active', active);
        c.hidden = !active;
      });
      // When the calculator tab becomes visible for the first time, the
      // indicator may have been placed while its panel was hidden (and
      // therefore had 0 layout). Re-snap it to the active pill so it lands
      // correctly the moment the panel is shown.
      if (name === 'recommender') {
        requestAnimationFrame(function () { positionIndicator(true); });
      }
    }

    tabs.forEach(function (t) {
      t.addEventListener('click', function () {
        activateTab(t.getAttribute('data-dg-size-guide-tab'));
      });
      t.addEventListener('keydown', function (e) {
        // Left/Right arrow navigation between tabs (standard tablist behavior).
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        var list = Array.prototype.slice.call(tabs);
        var idx = list.indexOf(t);
        var next = e.key === 'ArrowRight'
          ? list[(idx + 1) % list.length]
          : list[(idx - 1 + list.length) % list.length];
        next.focus();
        activateTab(next.getAttribute('data-dg-size-guide-tab'));
      });
    });

    // ---- Recommender -------------------------------------------------------
    var heightFt = root.querySelector('[data-dg-size-guide-height-ft]');
    var heightIn = root.querySelector('[data-dg-size-guide-height-in]');
    var weightInput = root.querySelector('[data-dg-size-guide-weight]');
    var fitRadios = root.querySelectorAll('[data-dg-size-guide-fit]');
    var resultEl = root.querySelector('[data-dg-size-guide-result]');

    // Tracks the last displayed "primary" recommendation so we know which
    // direction to slide the big size number when it changes (e.g. Tight S
    // -> Normal M -> Baggy L is a sequence of upward slides).
    var previousPrimary = null;

    function currentFit() {
      for (var i = 0; i < fitRadios.length; i++) {
        if (fitRadios[i].checked) return fitRadios[i].value;
      }
      return 'normal';
    }

    function prefersReducedMotion() {
      try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      } catch (_) { return false; }
    }

    /**
     * Builds the stable result markup (label + size slot + three pills + a
     * shared "indicator" element that slides between the active pill's
     * position to give a segmented-control feel).
     *
     * Only called when transitioning out of a "message" state so subsequent
     * updates can swap text content in place and animations work against
     * persistent elements instead of brand-new innerHTML each render.
     */
    function buildResultStructure() {
      resultEl.innerHTML =
        '<div class="dg-size-guide__result-label">We recommend</div>' +
        '<div class="dg-size-guide__result-size" data-dg-result-size></div>' +
        '<div class="dg-size-guide__result-all" data-dg-result-all>' +
          // Indicator first in DOM so pills stay on top in the natural paint
          // order; the CSS also pins z-indexes explicitly.
          '<span class="dg-size-guide__result-pill-indicator" data-dg-result-indicator aria-hidden="true"></span>' +
          buildPill('tight') +
          buildPill('normal') +
          buildPill('baggy') +
        '</div>';
    }

    function buildPill(key) {
      var label = key.charAt(0).toUpperCase() + key.slice(1);
      return '<span class="dg-size-guide__result-pill" data-dg-result-pill="' + key + '">' +
               '<em>' + label + '</em>' +
               '<strong data-dg-result-pill-value></strong>' +
             '</span>';
    }

    function showMessage(msg) {
      resultEl.innerHTML = '<div class="dg-size-guide__result-label">' + escapeHtml(msg) + '</div>';
      previousPrimary = null;
    }

    /**
     * Slide the size number. Web Animations API avoids the CSS
     * remove-class/force-reflow/add-class dance and plays cleanly even when
     * the class is already present from a previous trigger.
     *
     * @param {HTMLElement} el
     * @param {number} direction  +1 grew, -1 shrank, 0 first appearance / no change
     */
    function animateSize(el, direction) {
      if (prefersReducedMotion() || typeof el.animate !== 'function') return;

      var keyframes;
      var duration = 320;
      if (direction > 0) {
        // Growing size -> new number rises up into place (from below).
        keyframes = [
          { transform: 'translateY(36%)', opacity: 0 },
          { transform: 'translateY(0)',    opacity: 1 }
        ];
      } else if (direction < 0) {
        // Shrinking size -> new number drops down into place (from above).
        keyframes = [
          { transform: 'translateY(-36%)', opacity: 0 },
          { transform: 'translateY(0)',     opacity: 1 }
        ];
      } else {
        // First appearance / unknown direction -> gentle scale + fade.
        duration = 240;
        keyframes = [
          { transform: 'scale(0.92)', opacity: 0 },
          { transform: 'scale(1)',    opacity: 1 }
        ];
      }

      el.animate(keyframes, {
        duration: duration,
        easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
        fill: 'both'
      });
    }

    function updateResult() {
      var ft = heightFt ? parseFloat(heightFt.value) : NaN;
      var inches = heightIn ? parseFloat(heightIn.value) : NaN;
      var weight = weightInput ? parseFloat(weightInput.value) : NaN;

      // Accept either ft-only, in-only, or both; combine into total inches.
      var totalIn = NaN;
      if (Number.isFinite(ft) || Number.isFinite(inches)) {
        totalIn = (Number.isFinite(ft) ? ft : 0) * 12 + (Number.isFinite(inches) ? inches : 0);
      }

      var valid = Number.isFinite(totalIn) && totalIn > 0 && Number.isFinite(weight) && weight > 0;
      if (!valid) { showMessage('Enter your height and weight'); return; }

      var recs = getRecommendedSizes(totalIn, weight, config);
      if (!recs) { showMessage('Unable to calculate'); return; }

      // First valid render (or returning from a message state): rebuild structure.
      if (!resultEl.querySelector('[data-dg-result-size]')) {
        buildResultStructure();
      }

      var fit = currentFit();
      var primary = recs[fit] || recs.normal;

      // ---- Big size number: animate only when the value actually changes --
      var sizeEl = resultEl.querySelector('[data-dg-result-size]');
      if (sizeEl) {
        var changed = sizeEl.textContent !== primary;
        sizeEl.textContent = primary;
        if (changed) {
          var direction = 0;
          if (previousPrimary && Array.isArray(config.sizeOrder)) {
            var prevIdx = config.sizeOrder.indexOf(previousPrimary);
            var newIdx = config.sizeOrder.indexOf(primary);
            if (prevIdx !== -1 && newIdx !== -1) {
              if (newIdx > prevIdx) direction = 1;
              else if (newIdx < prevIdx) direction = -1;
            }
          }
          animateSize(sizeEl, direction);
        }
      }

      // ---- Tight/Normal/Baggy pills: persistent nodes, and a shared
      // indicator that slides between the active pill's position so the
      // red rim/glow tracks the fit selection instead of snapping.
      var indicator = resultEl.querySelector('[data-dg-result-indicator]');
      var firstPlacement = indicator && !indicator.classList.contains('is-visible');

      resultEl.querySelectorAll('[data-dg-result-pill]').forEach(function (p) {
        var key = p.getAttribute('data-dg-result-pill');
        p.classList.toggle('is-active', key === fit);
        var strong = p.querySelector('[data-dg-result-pill-value]');
        if (strong) strong.textContent = recs[key];
      });

      // Position after the class flip so the target pill's layout metrics
      // reflect its current (active) state. Skip animation on the first
      // placement so it pops into position instead of sliding in from 0,0.
      positionIndicator(firstPlacement);

      previousPrimary = primary;
    }

    /**
     * Pins the pill indicator to the currently-active pill using its
     * offset metrics relative to the shared result container (which is
     * position: relative). Setting left/top/width/height as inline styles
     * lets the CSS transition do the rest.
     *
     * @param {boolean} instant  Skip the CSS transition for this update
     *   (used on first placement and during window resizes so the indicator
     *   snaps rather than slides in from the previous position).
     */
    function positionIndicator(instant) {
      if (!resultEl) return;
      var indicator = resultEl.querySelector('[data-dg-result-indicator]');
      var activePill = resultEl.querySelector('[data-dg-result-pill].is-active');
      if (!indicator || !activePill) return;
      // If the calculator panel isn't laid out yet (hidden tab, 0-size
      // offsetParent), deferring avoids placing the indicator at 0,0.
      if (!activePill.offsetParent) return;

      if (instant) {
        indicator.style.transition = 'none';
      }
      indicator.style.left = activePill.offsetLeft + 'px';
      indicator.style.top = activePill.offsetTop + 'px';
      indicator.style.width = activePill.offsetWidth + 'px';
      indicator.style.height = activePill.offsetHeight + 'px';
      indicator.classList.add('is-visible');

      if (instant) {
        // Force reflow so the `transition: none` is committed before we
        // restore the stylesheet transition on the next update.
        void indicator.offsetWidth;
        indicator.style.transition = '';
      }
    }

    [heightFt, heightIn, weightInput].forEach(function (el) {
      if (el) el.addEventListener('input', updateResult);
    });
    fitRadios.forEach(function (r) {
      r.addEventListener('change', function () {
        // Keep the label chip's `is-selected` class in sync so the CSS picks it up.
        fitRadios.forEach(function (x) {
          var label = x.closest('.dg-size-guide__fit-option');
          if (label) label.classList.toggle('is-selected', x.checked);
        });
        updateResult();
      });
    });

    openBtn.setAttribute('aria-expanded', 'false');

    // Keep the indicator pinned when the viewport changes size (pills may
    // wrap on narrow screens). Snapping instead of sliding avoids a laggy
    // "chasing" animation during a drag-resize.
    window.addEventListener('resize', function () {
      var indicator = resultEl && resultEl.querySelector('[data-dg-result-indicator].is-visible');
      if (indicator) positionIndicator(true);
    });
  }

  function initAll() {
    document.querySelectorAll('[data-dg-size-guide]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Expose for debugging + any external callers (e.g. quickview) that want to
  // reuse the same recommender without instantiating the modal.
  window.DgSizeGuide = { getRecommendedSizes: getRecommendedSizes };
})();
